import {
  Avatar,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { colorTheme } from 'theme';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { useDispatch } from 'react-redux';
import { setEmailVerified } from 'store/onboarding';
import { setSnackBarFailed } from 'store/snackBar';
import LoadingButton from '@mui/lab/LoadingButton';
import { strings } from 'i18n/Strings';

import Logo from '../../assets/images/Logo100X132.png';

import { verifyCustomerEmailAndPhoneOtp } from 'service/AuthService';

function useQuery(): URLSearchParams {
  return new URLSearchParams(useLocation().search);
}

const OTPVerification = (): JSX.Element => {
  const MAX_CODE_LENGTH = 4;
  const query = useQuery();
  const email = query.get('email');
  const phone = query.get('phone');
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const isSmallScreen = useMediaQuery('(max-width:480px)');
  const [emailCode, setEmailCode] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [errorEmail, setErrorEmail] = useState('');
  const [errorPhone, setErrorPhone] = useState('');
  const [loader, setLoader] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);

  const matchIsNumeric = (character: string): boolean => {
    const isNumber = typeof character === 'number';
    return isNumber || !isNaN(Number(character));
  };

  const validateChar = (value: string): boolean => {
    if (!matchIsNumeric(value)) {
      setErrorEmail(strings.displayText.allowDigitsOnlyText);
      setErrorPhone(strings.displayText.allowDigitsOnlyText);
      return false;
    } else {
      setErrorEmail('');
      setErrorPhone('');
      return true;
    }
  };

  const handleOtp = (userOtp: string): void => {
    setEmailCode(userOtp);
  };

  const handlePhoneOtp = (userOtp: string): void => {
    setPhoneCode(userOtp);
  };

  const handleVerifyOtp = (): void => {
    if (emailCode !== '' && phoneCode !== '') {
      setLoader(true);
      verifyCustomerEmailAndPhoneOtp(emailCode, phoneCode, email, phone)
        .then((response) => {
          setLoader(false);
          setErrorEmail('');
          setErrorPhone('');
          setIsVerified(true);
        })
        .catch((err) => {
          dispatch(
            setSnackBarFailed({
              snackBarMessage:
                err?.response?.data?.message ||
                strings.displayText.unknownErrorText,
            }),
          );
          if (err?.response?.status === 400) {
            setErrorEmail(err?.response?.data?.message);
            setErrorPhone(err?.response?.data?.message);
          } else {
            setErrorEmail(strings.displayText.unknownErrorText);
            setErrorPhone(strings.displayText.unknownErrorText);
          }
          setLoader(false);
          dispatch(setEmailVerified(false));
        });
    } else {
      setErrorEmail(
        emailCode === ''
          ? 'Enter the email verification code'
          : 'Enter the phone verification code',
      );
    }
  };

  const styles = {
    container: {
      backgroundColor: colors.greenAccent[500],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      minHeight: '100vh',
      paddingLeft: isSmallScreen ? '20px' : '0px',
      paddingRight: isSmallScreen ? '20px' : '0px',
    },
    mainBox: {
      display: 'flex',
      flexDirection: 'column',
      border: `2px solid ${colors.greenAccent[500]}`,
      paddingLeft: isSmallScreen ? '16px' : '160px',
      paddingRight: isSmallScreen ? '16px' : '160px',
      boxShadow: 3,
      borderRadius: 3,
      backgroundColor: 'white',
      width: '100%',
      maxWidth: '700px',
    },
    avatar: {
      width: 100,
      height: 100,
      p: 2,
      bgcolor: colors.primaryBg[900],
      boxShadow: 3,
      position: 'relative',
      top: -48,
      left: isSmallScreen ? 'calc(50% - 50px)' : '36%',
    },
    avatarImg: {
      width: 46,
      height: 56,
    },
    otpTypho: {
      fontFamily: 'Source Sans Pro !important',
      fontWeight: 700,
      color: colors.greenAccent[500],
    },
    text: {
      fontFamily: 'Source Sans Pro !important',
      fontWeight: 700,
      color: colors.greenAccent[500],
      textAlign: 'center',
    },
    sucessText: {
      fontFamily: 'Source Sans Pro !important',
      fontWeight: 700,
      color: colors.greenAccent[500],
      textAlign: 'center',
      alignItems: 'center',
      justifyContent: 'center',
      width: '400px',
      height: '150px',
      whiteSpace: 'nowrap',
    },

    text1: {
      fontFamily: 'Source Sans Pro !important',
      color: colors.greenAccent[500],
      textAlign: 'center',
    },
    pleaseEnterOtpTypho: {
      color: colors.gray[500],
      fontWeight: 400,
      fontSize: 14,
      lineHeight: 2,
      textAlign: 'center',
    },
    errorEmail: {
      color: 'red',
      fontWeight: 400,
      fontSize: 14,
      textAlign: 'center',
    },

    loadingBtn: {
      mt: 3,
      mb: 2,
      bgcolor: colors.greenAccent[500],
      borderRadius: 2,
      py: 1.5,
    },
  };

  return (
    <div style={styles.container}>
      <Box sx={styles.mainBox}>
        <Avatar alt="zen pensions logo" sx={styles.avatar}>
          <img style={styles.avatarImg} src={Logo} />
        </Avatar>

        {isVerified ? (
          <>
            <Box pb={4} sx={{ width: '500px', height: '150px' }} />
            <Typography component="h1" variant="h1" sx={styles.sucessText}>
              {strings.displayText.accountVerified}
            </Typography>
            <Box pb={18} />
          </>
        ) : (
          <>
            <Typography component="h1" variant="h4" sx={styles.text}>
              {strings.displayText.sentOtptoEmailAndPhone}
            </Typography>

            <Typography component="h6" variant="h6" sx={styles.text1}>
              {strings.displayText.pleaseEnterCode}
            </Typography>

            <Box pb={2} />

            <Typography component="h1" variant="h4" sx={styles.otpTypho}>
              {strings.displayText.emailOtp}
            </Typography>

            <Box pb={2} />
            <MuiOtpInput
              TextFieldsProps={{ autoComplete: 'off' }}
              value={emailCode}
              onChange={handleOtp}
              length={MAX_CODE_LENGTH}
              validateChar={validateChar}
            />
            <Box pb={4} />

            <Typography component="h1" variant="h4" sx={styles.otpTypho}>
              {strings.displayText.phoneOtp}
            </Typography>

            <Box pb={2} />
            <MuiOtpInput
              TextFieldsProps={{ autoComplete: 'off' }}
              value={phoneCode}
              onChange={handlePhoneOtp}
              length={MAX_CODE_LENGTH}
              validateChar={validateChar}
            />
            <Box pb={2} />
            {errorEmail.length > 0 && errorPhone.length > 0 && (
              <Typography component="h5" variant="h5" sx={styles.errorEmail}>
                {errorEmail}
              </Typography>
            )}

            <Box pb={3} />
            <LoadingButton
              onClick={handleVerifyOtp}
              type="submit"
              fullWidth
              variant="contained"
              loading={loader}
              loadingPosition="end"
              sx={styles.loadingBtn}
            >
              <Typography component="h1" variant="h5">
                {strings.displayText.verifyOtp}
              </Typography>
            </LoadingButton>
            <Box pb={3} />
          </>
        )}
      </Box>
    </div>
  );
};

export default OTPVerification;
